import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import api from '../../utils/api';
import { setUnreadCount, decrementUnreadCount } from '../../store/slices/notificationSlice';

export default function NotificationBell({ dark = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const unreadCount = useSelector((state) => state.notifications?.unreadCount ?? 0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      const count = res.data?.count ?? res.data?.unreadCount ?? 0;
      dispatch(setUnreadCount(count));
    } catch (e) {
      console.warn('notifications unread-count', e);
    }
  }, [dispatch]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications', { params: { limit: 20 } });
      const list = res.data?.notifications ?? res.data ?? [];
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.warn('notifications list', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchList();
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkRead = async (notification) => {
    if (!notification?.id) return;
    try {
      await api.put(`/notifications/${notification.id}/read`);
      if (!notification.isRead) {
        dispatch(decrementUnreadCount());
      }
      setItems((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
      );
      if (notification.link) {
        handleClose();
        navigate(notification.link.startsWith('/') ? notification.link : `/${notification.link}`);
      }
    } catch (e) {
      console.warn('mark read', e);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Notifications"
        sx={
          dark
            ? {
                color: '#94a3b8',
                borderRadius: '6px',
                p: '4px 8px',
                '&:hover': { bgcolor: '#1e293b' },
              }
            : { color: 'inherit' }
        }
      >
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : null}
          color="error"
          sx={
            dark
              ? {
                  '& .MuiBadge-badge': {
                    border: `2px solid ${'#0f172a'}`,
                    minWidth: 8,
                    height: 8,
                  },
                }
              : undefined
          }
        >
          <NotificationsIcon sx={dark ? { fontSize: 16 } : undefined} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Notifications
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No notifications yet
          </Typography>
        ) : (
          <List dense disablePadding>
            {items.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleMarkRead(n)}
                sx={{ bgcolor: n.isRead ? 'transparent' : 'action.hover' }}
              >
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      <Typography variant="caption" component="span" display="block">
                        {n.message}
                      </Typography>
                      {n.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
}
